<template>
  <div class="form-outline pa-3">
    <form-legend :field="field" />
    <v-text-field
      :ref="'qrcode-scanner-' + field.cid"
      v-model="fieldValue"
      :type="field.field_type"
      :rules="getFieldRules({ field, required: field.required })"
      :hint="hint"
      :disabled="true"
      :readonly="true"
      class="mb-3 n-form-field"
      :append-icon="scanCompleted ? 'mdi-check-circle' : 'mdi-qrcode-scan'"
      persistent-hint
      outlined
      dense
    >
      <template v-slot:message="{ message }">
        <legend v-html="message"></legend>
      </template>
      <template v-slot:append-outer v-if="field.field_options.units">
        {{ field.field_options.units }}
      </template>
    </v-text-field>

    <v-btn @click="openScanner" class="mb-3" rounded color="primary-action">{{ $t('forms.editors.qrCodeScanner.startScanner') }}</v-btn>

    <v-dialog v-model="showScanner" persistent max-width="600px">
      <v-card>
        <v-card-title>
          {{ $t('forms.editors.qrCodeScanner.qrCodeScanner') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="closeScanner">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row align="center" justify="center">
            <v-col cols="12" class="text-center">
              <video ref="qrVideo" width="500" height="500" style="max-width: 100%"></video>
            </v-col>
          </v-row>
          <v-file-input
            capture="user"
            :label="$t('forms.editors.qrCodeScanner.uploadFile')"
            accept="image/*"
            prepend-icon="mdi-image"
            @change="scanFile"
          ></v-file-input>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formsMixin from '../formsMixin';
import jsQR from 'jsqr';
import FormLegend from '@/modules/forms/FormLegend.vue';

export default {
  components: { FormLegend },
  mixins: [formsMixin],
  props: {
    field: { type: Object, required: true },
    formModel: { type: Object, required: true },
    mediasModel: { type: Array, required: true },
    readonly: { type: Boolean, default: false },
    mode: { type: String, default: 'light' },
  },
  data() {
    return {
      fieldValue: this.formModel[this.field.cid] ? this.formModel[this.field.cid] : '',
      showScanner: false,
      video: null,
      scanCompleted: false,
    };
  },
  computed: {
    hint() {
      if (this.field.field_options.auto_fill) {
        return 'Automatisch ausgefülltes Feld "' + this.$t('forms.auto_fill.option.' + this.field.field_options.auto_fill) + '"';
      }
      return this.field_options.description;
    },
    field_options() {
      return this.field.field_options;
    },
  },
  methods: {
    async startScanner() {
      try {
        await this.$nextTick();

        this.video = this.$refs.qrVideo;
        if (!this.video) {
          console.error('Video element not found');
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        this.video.srcObject = stream;
        this.video.play();
        requestAnimationFrame(this.scanVideo);
      } catch (error) {
        console.error('Error accessing camera:', error);
      }
    },

    openScanner() {
      this.showScanner = true;
      this.scanCompleted = false;
      this.fieldValue = '';
      this.startScanner();
    },

    scanVideo() {
      if (this.isVideoReadyToScan()) {
        this.processVideoFrame();
      } else {
        requestAnimationFrame(this.scanVideo);
      }
    },

    isVideoReadyToScan() {
      return this.video.readyState === this.video.HAVE_ENOUGH_DATA;
    },

    processVideoFrame() {
      const canvas = this.createCanvasForVideoFrame();
      const context = canvas.getContext('2d');
      context.drawImage(this.video, 0, 0, canvas.width, canvas.height);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const qrCodeData = jsQR(imageData.data, imageData.width, imageData.height);

      if (qrCodeData) {
        this.handleScanSuccess(qrCodeData.data);
      } else {
        requestAnimationFrame(this.scanVideo);
      }
    },

    createCanvasForVideoFrame() {
      const canvas = document.createElement('canvas');
      canvas.width = this.video.videoWidth;
      canvas.height = this.video.videoHeight;
      return canvas;
    },

    scanFile(fileInput) {
      const file = Array.isArray(fileInput) ? fileInput[0] : fileInput;
      if (!file) {
        console.debug('No file selected for scanning.');
        return;
      }

      this.readFileAsDataURL(file);
    },

    readFileAsDataURL(file) {
      const fileReader = new FileReader();

      fileReader.onload = (event) => {
        this.loadImageFromDataURL(event.target.result);
      };

      fileReader.readAsDataURL(file);
    },

    loadImageFromDataURL(dataURL) {
      const image = new Image();

      image.onload = () => {
        this.processImageForQRCode(image);
      };

      image.src = dataURL;
    },

    processImageForQRCode(image) {
      const canvas = document.createElement('canvas');
      canvas.width = image.width;
      canvas.height = image.height;

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);

      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const qrCodeData = jsQR(imageData.data, imageData.width, imageData.height);

      if (qrCodeData) {
        this.handleScanSuccess(qrCodeData.data);
      } else {
        console.debug('No QR code found in the image.');
      }
    },

    handleScanSuccess(decodedText) {
      this.fieldValue = decodedText;
      this.scanCompleted = true;
      this.closeScanner(false);
    },

    closeScanner(manual = true) {
      this.showScanner = false;
      if (this.video && this.video.srcObject) {
        this.video.srcObject.getTracks().forEach((track) => track.stop());
      }
      if (manual) {
        this.scanCompleted = false;
        this.fieldValue = '';
      }
    },
  },
  watch: {
    fieldValue(newValue) {
      if (newValue) {
        this.$refs['qrcode-scanner-' + this.field.cid].validate();
      }
      this.$emit('formUpdated', this.field.cid, newValue);
    },
    formModel(newValue) {
      const value = newValue[this.field.cid] ? newValue[this.field.cid] : '';
      if (this.fieldValue != value) {
        this.fieldValue = value;
      }
    },
  },
};
</script>
